import {
  getGetNotificationsEmailMessagesQueryKey,
  useGetNotificationsEmailMessages,
  usePutNotificationsEmailMessagesMessageKey,
} from '@agos/api-client/notifications'
import { useGetCentralBranding } from '@agos/api-client/platform'
import { T, franchiseNameOf, tt } from '@agos/shared'
import { Spinner, useToast } from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { Mail, RotateCcw, Save } from 'lucide-react'
import { useState } from 'react'

import {
  draftOf,
  isDirty,
  isOverridden,
  missingRequired,
  placeholderHint,
  bodyModeLabel,
  bodyModeNote,
  footerNote,
  htmlBody,
  previewOf,
  unknownPlaceholders,
  type TemplateDraft,
} from '../message-templates'
import {
  bodyIsHeadingAndParagraph,
  emailMessagesOf,
  emailPayloadOf,
  headingOf,
  paragraphOf,
  type EmailMessage,
} from './email-messages'

/**
 * The wording of each e-mail the system sends - FRONTEND.md §9.322, BACKEND.md §10A.112.
 *
 * **The owner:** *"Make every email ETC we are using, to be configurable in that nastavenia -> EMAIL,
 * but make it also for all SMS and pushups..."*
 *
 * **Deliberately the same editor as the Pushup tab** (§9.298), down to the layout: an owner who has
 * written a push message should not have to learn a second screen. The rules come from
 * `../message-templates`, shared with it.
 *
 * **The default is a placeholder, never a value.** An input pre-filled with the built-in text cannot
 * tell *"I left it alone"* from *"I typed exactly that"*, and the first save would freeze every
 * untouched message against a later improvement to the wording.
 *
 * **Saved on a button, not on every keystroke** - FB-145 is on the notifications page for exactly
 * this. A subject and a body are edited together and are wrong apart: a half-saved message is one
 * that goes out.
 *
 * **Its own component rather than rows on the page above**, because that page reads
 * `loyalty_settings` and this reads `notification_templates`. Two stores in one form is how a save
 * of one silently reverts the other.
 */
export function EmailMessageEditor({ writable }: { writable: boolean }) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { data, isLoading } = useGetNotificationsEmailMessages()
  /* §9.700 - the preview names this franchise, as the server's `{restauracia}` does. It named *Pizza Majstro
     Nesvady* for every franchise. A failed read here degrades a preview to the neutral stand-in, which is why
     it has no failure branch of its own. */
  const { data: branding } = useGetCentralBranding()
  const restaurant = franchiseNameOf(branding)
  const save = usePutNotificationsEmailMessagesMessageKey()
  const [drafts, setDrafts] = useState<Record<string, TemplateDraft>>({})
  const [savingKey, setSavingKey] = useState<string | null>(null)

  const messages = emailMessagesOf(data)

  const draftFor = (message: EmailMessage) => drafts[message.key] ?? draftOf(message)

  const submit = async (message: EmailMessage, draft: TemplateDraft) => {
    setSavingKey(message.key)
    try {
      await save.mutateAsync({ messageKey: message.key, data: emailPayloadOf(draft) })
      await queryClient.invalidateQueries({
        queryKey: getGetNotificationsEmailMessagesQueryKey(),
      })
      setDrafts((current) => {
        const next = { ...current }
        delete next[message.key]
        return next
      })
      toast({ title: tt('Text uložený'), description: message.label })
    } catch {
      // The draft stays exactly as typed. A refused save that clears the box loses the wording.
      toast({ variant: 'destructive', title: tt('Uloženie zlyhalo'), description: message.label })
    } finally {
      setSavingKey(null)
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    )
  }

  return (
    <div
      className="bg-card border border-border rounded-2xl overflow-hidden"
      data-email-texts={messages.length}
    >
      <div className="px-5 py-3 border-b border-border bg-muted/20">
        <h2 className="font-bold text-foreground text-sm flex items-center gap-2">
          <Mail className="w-4 h-4 text-primary" /> <T>Texty e-mailov</T>
        </h2>
        <p className="text-xs text-muted-foreground mt-1">
          <T>
            Prázdne pole znamená pôvodný text. Rovnaké premenné ako pri SMS a push - vypíšu sa zo
            skutočnej objednávky alebo zákazníka.
          </T>
        </p>
      </div>

      <div className="divide-y divide-border">
        {messages.map((message) => {
          const draft = draftFor(message)
          const dirty = isDirty(message, draft)
          const preview = previewOf(message, draft, restaurant)
          const split = bodyIsHeadingAndParagraph(message.key)
          const strange = [
            ...unknownPlaceholders(draft.first, message.placeholders),
            ...unknownPlaceholders(draft.body, message.placeholders),
          ]
          const required = missingRequired(message.key, draft.body)

          return (
            <div key={message.key} className="px-5 py-4 space-y-2" data-email-message={message.key}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-sm text-foreground">{message.label}</span>
                {isOverridden(message) && (
                  <span
                    className="text-[10px] font-bold px-2 py-0.5 rounded-full border border-primary/40 bg-primary/10 text-primary"
                    data-email-overridden
                  >
                    <T>vlastný text</T>
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">{message.desc}</p>

              <input
                value={draft.first}
                placeholder={message.defaultFirst}
                aria-label={`${tt('Predmet')} - ${message.label}`}
                disabled={!writable}
                onChange={(change) =>
                  setDrafts((current) => ({
                    ...current,
                    [message.key]: { ...draft, first: change.target.value },
                  }))
                }
                className="w-full px-3 py-2 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50"
              />
              <textarea
                value={draft.body}
                placeholder={message.defaultBody}
                aria-label={`${tt('Text')} - ${message.label}`}
                rows={split ? 3 : 8}
                disabled={!writable}
                onChange={(change) =>
                  setDrafts((current) => ({
                    ...current,
                    [message.key]: { ...draft, body: change.target.value },
                  }))
                }
                className="w-full px-3 py-2 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20 resize-y disabled:opacity-50"
              />

              {/* §9.340 - which of the two things this body is, said in words. The rule is one
                * line in `EmailGateway.asHtml` and was invisible from here, so somebody pasting an
                * export from an e-mail builder had no way to tell whether it would work. */}
              <p className="text-[11px] text-muted-foreground" data-email-body-mode={message.key}>
                <span className="font-semibold">{bodyModeLabel(draft.body)}</span>{' '}
                {bodyModeNote(draft.body)}
              </p>
              {htmlBody(draft.body) && (
                <p className="text-[11px] text-muted-foreground" data-email-footer-note>
                  {footerNote()}
                </p>
              )}

              <p className="text-[11px] text-muted-foreground" data-email-placeholders>
                {placeholderHint(message.placeholders)}
              </p>
              {strange.length > 0 && (
                <p className="text-[11px] text-amber-500" data-email-unknown={strange.length}>
                  {tt('Toto sa nenahradí a odíde tak, ako je napísané')}:{' '}
                  {strange.map((name) => `{${name}}`).join(', ')}
                </p>
              )}
              {required.length > 0 && (
                <p className="text-[11px] text-red-500" data-email-missing={message.key}>
                  {/* Warned, not refused: the sentence around the link is the franchise's to
                      write. What must not happen is a reset e-mail with no link in it. */}
                  {tt('Bez tejto premennej e-mail nebude fungovať')}:{' '}
                  {required.map((name) => `{${name}}`).join(', ')}
                </p>
              )}

              {/* A template is a sentence with holes in it, and the mistakes are all in the holes. */}
              <div
                className="rounded-xl border border-border bg-muted/30 px-3 py-2"
                data-email-preview={message.key}
              >
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  <T>Ukážka</T>
                </p>
                <p className="text-sm font-semibold text-foreground">{preview.first}</p>
                {htmlBody(preview.body) ? (
                  /* §9.340 - rendered, because a designed e-mail shown as escaped markup reads as
                   * broken. **An `iframe` and not `dangerouslySetInnerHTML`**: the value is the
                   * owner's own template, but it is a value out of the database rendered inside the
                   * manager - `sandbox=""` costs one attribute where trusting it costs a stored-XSS
                   * surface on an admin screen. Empty sandbox means no scripts, no forms and no
                   * same-origin access. */
                  <iframe
                    title={`${tt('Ukážka')}: ${message.key}`}
                    data-email-preview-html={message.key}
                    sandbox=""
                    srcDoc={preview.body}
                    className="w-full h-64 mt-1 rounded-lg border border-border bg-white"
                  />
                ) : split ? (
                  <>
                    {/* §10A.112's rule, shown rather than explained: the first line becomes the
                        heading of the message and the rest becomes the paragraph under it. */}
                    <p className="text-sm text-foreground mt-1" data-email-preview-heading>
                      {headingOf(preview.body)}
                    </p>
                    <p className="text-xs text-muted-foreground whitespace-pre-line">
                      {paragraphOf(preview.body)}
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      <T>Položky, sumy a pätička sa dopĺňajú automaticky.</T>
                    </p>
                  </>
                ) : (
                  <p className="text-xs text-muted-foreground whitespace-pre-line">
                    {preview.body}
                  </p>
                )}
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={!writable || !dirty || savingKey === message.key}
                  onClick={() => void submit(message, draft)}
                  data-email-save={message.key}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-primary/40 bg-primary/10 text-primary text-xs font-semibold disabled:opacity-40"
                >
                  <Save className="w-3.5 h-3.5" /> <T>Uložiť text</T>
                </button>
                <button
                  type="button"
                  disabled={
                    !writable ||
                    (!isOverridden(message) && draft.first === '' && draft.body === '')
                  }
                  onClick={() => void submit(message, { first: '', body: '' })}
                  data-email-reset={message.key}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-muted-foreground text-xs font-semibold disabled:opacity-40"
                >
                  <RotateCcw className="w-3.5 h-3.5" /> <T>Vrátiť pôvodný text</T>
                </button>
                {savingKey === message.key && <Spinner />}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
